<template>
    <v-dialog v-model="showDialog" max-width="550">
        <template v-slot:activator="{ on, attrs }">
            <v-btn icon small dark v-bind="attrs" v-on="on" color="primary">
                <font-awesome-icon :icon="icons.config" class="fa-fw fa-lg"></font-awesome-icon>
            </v-btn>
        </template>
        <v-card>
            <v-card-title>{{ $t('friendZone.manageSharingDetailsDialog.title') }}</v-card-title>
            <v-list flat subheader three-line>
                <v-subheader><span v-html="$t('friendZone.manageSharingDetailsDialog.description', { friendName: fullName })"></span></v-subheader>
                <v-divider></v-divider>
                <div class="options">
                    <v-checkbox v-model="portfolioSharingOptions.active" :label="$t('friendZone.manageSharingDetailsDialog.portfolios.label')" hide-details></v-checkbox>
                    <v-checkbox v-model="transactionSharingOptions.active" :label="$t('friendZone.manageSharingDetailsDialog.transactions.label')" hide-details></v-checkbox>
                    <div v-if="transactionSharingOptions.active" class="subOptions">
                        <v-radio-group v-model="transactionSharingOptions.showAll" hide-details>
                            <v-radio :label="$t('friendZone.manageSharingDetailsDialog.transactions.share.all')" :value="true"></v-radio>
                            <v-radio :label="$t('friendZone.manageSharingDetailsDialog.transactions.share.limit')" :value="false"></v-radio>
                        </v-radio-group>
                        <div v-if="!transactionSharingOptions.showAll" class="subOptions optionsWithValues">
                            <v-radio-group v-model="transactionSharingOptions.limitType" hide-details>
                                <v-radio :label="$t('friendZone.manageSharingDetailsDialog.transactions.options.sinceDateOnly')" value="sinceDate"></v-radio>
                                <v-radio :label="$t('friendZone.manageSharingDetailsDialog.transactions.options.lastNMonths')" value="lastNMonths"></v-radio>
                                <v-radio :label="$t('friendZone.manageSharingDetailsDialog.transactions.options.lastNTransactions')" value="lastNTransactions"></v-radio>
                            </v-radio-group>
                            <div class="optionsValues">
                                <v-menu ref="sinceDateMenu" v-model="sinceDateMenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto" :disabled="sinceDateDisabled">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="transactionSharingOptions.scopeLimit.sinceDateOnly" dense outlined :label="$t('friendZone.manageSharingDetailsDialog.transactions.options.sinceDate')" prepend-inner-icon="mdi-calendar" readonly :disabled="sinceDateDisabled" v-bind="attrs" v-on="on" hide-details></v-text-field>
                                    </template>
                                    <v-date-picker v-model="transactionSharingOptions.scopeLimit.sinceDateOnly" @input="sinceDateMenu = false" :readonly="sinceDateDisabled"></v-date-picker>
                                </v-menu>
                                <v-text-field v-model.number="transactionSharingOptions.scopeLimit.lastNMonths" dense outlined :label="$t('friendZone.manageSharingDetailsDialog.transactions.options.numberOfMonths')" type="number" min="1" :disabled="numberOfMonthsDisabled" hide-details></v-text-field>
                                <v-text-field v-model.number="transactionSharingOptions.scopeLimit.lastNTransactions" dense outlined :label="$t('friendZone.manageSharingDetailsDialog.transactions.options.numberOfTransactions')" type="number" min="1" :disabled="numberOfTransactionsDisabled" hide-details></v-text-field>
                            </div>
                        </div>
                    </div>
                </div>
                <v-divider></v-divider>
                <div class="options">
                    <div class="subheader">{{$t('friendZone.manageSharingDetailsDialog.transactions.options.privacyOptionsLabel')}}</div>
                    <v-checkbox v-model="confidentialSharingOptions.shareAmount" :disabled="shareAmountDisabled" :label="$t('friendZone.manageSharingDetailsDialog.confidential.amounts.label')" hide-details></v-checkbox>
                </div>
            </v-list>
            <v-card-actions>
                <SaveCancelButtons align="end" @saveAsked="submit" @cancelAsked="cancel"></SaveCancelButtons>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { faCog } from '@fortawesome/pro-regular-svg-icons';
import SaveCancelButtons from '../../components/controls/SaveCancelButtons.vue';
import { updateFriendshipSharingOptions } from '../../services/SharingService';
import DateHelper from '../../infra/DateHelper';

export default {
	components: { SaveCancelButtons },
    props: {
        friend: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            icons: {
                config: faCog
            },
            showDialog: false,
            sinceDateMenu: undefined,
            portfolioSharingOptions: {
                active: false
            },
            confidentialSharingOptions: {
                shareAmount: false
            },
            transactionSharingOptions: {
                active: false,
                showAll: true,
                limitType: 'lastNTransactions',
                scopeLimit: {
                    lastNTransactions: 10,
                    sinceDateOnly: DateHelper.today(),
                    lastNMonths: 1
                }
            }
        }
    },
    computed: {
        fullName() {
            return this.friend ? this.friend.fullName : '';
        },
        shareAmountDisabled() {
            return !(this.portfolioSharingOptions.active || this.transactionSharingOptions.active);
        },
        sinceDateDisabled() {
            return !(this.transactionSharingOptions.active && !this.transactionSharingOptions.showAll && this.transactionSharingOptions.limitType === 'sinceDate');
        },
        numberOfMonthsDisabled() {
            return !(this.transactionSharingOptions.active && !this.transactionSharingOptions.showAll && this.transactionSharingOptions.limitType === 'lastNMonths');
        },
        numberOfTransactionsDisabled() {
            return !(this.transactionSharingOptions.active && !this.transactionSharingOptions.showAll && this.transactionSharingOptions.limitType === 'lastNTransactions');
        }
    },
    methods: {
        submit() {
            const portfolioSharingDetails = {
                portfolios: {
                    active: this.portfolioSharingOptions.active
                },
                confidential: {
                    shareAmount: this.confidentialSharingOptions.shareAmount
                },
                transactions: {
                    active: this.transactionSharingOptions.active,
                    showAll: this.transactionSharingOptions.showAll,
                    scopeLimit: {
                        limitType: this.transactionSharingOptions.limitType,
                        lastNTransactions: this.transactionSharingOptions.scopeLimit.lastNTransactions,
                        sinceDateOnly: this.transactionSharingOptions.scopeLimit.sinceDateOnly,
                        lastNMonths: this.transactionSharingOptions.scopeLimit.lastNMonths
                    }
                }
            };
            updateFriendshipSharingOptions({ friendUserId: this.friend.id, portfolioSharingDetails })
            .then(() => {
                this.$emit('updated', { portfolioSharingDetails });
            })
            .catch((error) => {
                console.error('Error updating friendship sharing options:', error);
            })
            .finally(() => {
                this.showDialog = false;
            });
        },
        cancel() {
            this.showDialog = false;
        }
    },
    created() {
        if (this.friend && this.friend.portfolioSharingDetails) {
            this.portfolioSharingOptions.active = this.friend.portfolioSharingDetails.portfolios ? this.friend.portfolioSharingDetails.portfolios.active : false;
            this.confidentialSharingOptions.shareAmount = this.friend.portfolioSharingDetails.confidential ? this.friend.portfolioSharingDetails.confidential.shareAmount : false;
            this.transactionSharingOptions.active = this.friend.portfolioSharingDetails.transactions ? this.friend.portfolioSharingDetails.transactions.active : false;
            this.transactionSharingOptions.showAll = this.friend.portfolioSharingDetails.transactions.showAll;
            this.transactionSharingOptions.limitType = this.friend.portfolioSharingDetails.transactions.scopeLimit.limitType || 'lastNTransactions';
            this.transactionSharingOptions.scopeLimit.lastNTransactions = this.friend.portfolioSharingDetails.transactions.scopeLimit.lastNTransactions || 10;
            this.transactionSharingOptions.scopeLimit.sinceDateOnly = this.friend.portfolioSharingDetails.transactions.scopeLimit.sinceDateOnly || DateHelper.today();
            this.transactionSharingOptions.scopeLimit.lastNMonths = this.friend.portfolioSharingDetails.transactions.scopeLimit.lastNMonths || 1;
        }
    }
}
</script>

<style lang="scss" scoped>
.options{
    padding: 16px;
    .subheader{
        font-size: 0.875rem;
        color: rgba(0, 0, 0, 0.6);
    }
    ::v-deep .v-input--selection-controls:first-of-type {
        margin-top: 0px;
    }
    .subOptions{
        display: flex;
        flex-direction: column;
        padding-left: 24px;
    }
    .optionsWithValues{
        gap: 16px;
        flex-direction: row;
        justify-content: space-between;
        ::v-deep .v-input--radio-group__input{
            gap: 24px;
        }
        .optionsValues{
            display: flex;
            flex-direction: column;
            gap: 15px;
        }
    }
}
</style>
